import { SelectFormFieldOption } from '@lessonup/ui-components';
import { TFunction } from 'i18next';
import { compact } from 'lodash';
import {
  GeneratedLessonDocument,
  GeneratedLessonDocumentPartial,
  GenerateMultipleChoiceQuestionsPayload,
  GeneratePollPayload,
  Language,
} from '../../../types/graphql';
import { noPhaseNumber } from '../../../utils/learningPhases/LearningPhase';

import { MaiaLessonType, textListToHTMLList } from '../ChatGPTGeneratorFeature.utils';
import { GeneratedResultItem } from '../components/Wizard/ReviewStep/reviewStepUtils';
import {
  LessonResponse,
  OpenQuestionResponse,
  OptionalLessonSettings,
  PollResponse,
  QuizResponse,
  ResponseSelection,
  SlideResponse,
  WordWebResponse,
} from '../model';

const defaultLanguage: Language = 'EN_UK';

const singleSlideOption = (count: number) => {
  return { label: `${count}`, value: count };
};

export const giveSelectionOptions = (selectCount: number, startCount = 1): SelectFormFieldOption<number>[] => {
  const slideOptions = [];
  for (let i = startCount; i <= selectCount; i++) {
    slideOptions.push(singleSlideOption(i));
  }
  return slideOptions;
};

export const getLanguageOptions = (t: TFunction): SelectFormFieldOption<Language>[] => [
  { label: t('en_uk'), value: 'EN_UK' },
  { label: t('en_us'), value: 'EN_US' },
  { label: t('fr'), value: 'FR' },
  { label: t('nl'), value: 'NL' },
  { label: t('de'), value: 'DE' },
  { label: t('es'), value: 'ES' },
  { label: t('cy'), value: 'CY' },
  { label: t('de'), value: 'DE' },
  { label: t('gd'), value: 'GD' },
  { label: t('hi'), value: 'HI' },
  { label: t('it'), value: 'IT' },
  { label: t('ja'), value: 'JA' },
  { label: t('mn'), value: 'MN' },
  { label: t('pt'), value: 'PT' },
  { label: t('zh'), value: 'ZH' },
];

export const generateTempResultId = (string: string) => Buffer.from(string).toString('base64');

export function mapGraphQLtoQuizResponse(document: GenerateMultipleChoiceQuestionsPayload): QuizResponse {
  const list = mapQuizResponse(document);
  return {
    type: 'quiz',
    list: compact(list),
  };
}

export function mapGraphQLtoPollResponse(document: GeneratePollPayload): PollResponse {
  const list = mapPollResponse(document);
  return {
    type: 'poll',
    list: compact(list),
  };
}

export function mapGraphQLtoLessonResponse(
  document: GeneratedLessonDocument | GeneratedLessonDocumentPartial | undefined,
  optionalSettings: OptionalLessonSettings,
  t: TFunction,
  outputLanguage: Language
): LessonResponse {
  const list = document ? mapLessonResponse(document, optionalSettings, t, outputLanguage) : [];

  return {
    type: 'lesson',
    name: document?.name ?? '',
    list: compact(list),
    settings: optionalSettings,
  };
}

export const mapLessonResponseToGeneratedResultList = (
  list: LessonResponse['list'],
  lessonType: MaiaLessonType
): GeneratedResultItem[] => {
  return compact(
    list.map((item) => {
      if (item.type === 'slide') {
        const slide: GeneratedResultItem = {
          tempId: item.tempId,
          phaseNumber: item.phaseNumber,
          content: {
            type: 'slide',
            header: item.header,
            layout: item.layout,
            text: lessonType === 'contextLesson' ? textListToHTMLList(item.text) : item.text,
            note: item.note,
          },
        };
        return slide;
      }
      if (item.type === 'openQuestion') {
        const openQuestion: GeneratedResultItem = {
          tempId: item.tempId,
          phaseNumber: item.phaseNumber,
          content: {
            type: 'openQuestion',
            question: item.question,
            note: item.note,
          },
        };
        return openQuestion;
      }
      if (item.type === 'wordweb') {
        const wordweb: GeneratedResultItem = {
          tempId: item.tempId,
          phaseNumber: item.phaseNumber,
          content: {
            type: 'wordweb',
            word: item.word,
          },
        };
        return wordweb;
      }
      console.warn('unknown type', item);
    })
  );
};

const mapLessonResponse = (
  document: GeneratedLessonDocument | GeneratedLessonDocumentPartial,
  optionalSettings: OptionalLessonSettings,
  t: TFunction,
  outputLanguage: Language
): ResponseSelection<LessonResponse> | undefined => {
  const mappedSlides = compact(
    document.slides?.map((item, index) => {
      if (item.__typename === 'GeneratedSlide') {
        const slide: SlideResponse = {
          type: 'slide',
          tempId: `slide-${index}-${simpleIdentifierForTitle(item.title)}`,
          layout: 'text',
          header: item.title,
          text: item.body,
          note: optionalSettings.enableTeacherNotes && item.notes ? item.notes : '',
          phaseNumber: 2000,
        };
        return slide;
      } else {
        console.warn('unknown type', item.__typename);
        return null;
      }
    })
  );

  const [objectiveSlide, ...slides] = mappedSlides;
  if (objectiveSlide) {
    objectiveSlide.phaseNumber = 1000;
  }

  const lessonResponseWithPhaseNumbers: ResponseSelection<LessonResponse> = compact([
    coverSlide(document?.name ?? undefined),
    objectiveSlide,
    optionalSettings.enablePreviousKnowledge
      ? activatePreviousKnowledgeSlide(document?.introQuestion ?? undefined)
      : null,
    ...slides,
    ...(optionalSettings.enableExitTicket
      ? getExitTicketSlideData(t, optionalSettings.enableTeacherNotes, outputLanguage)
      : []),
  ]);

  return optionalSettings.enableLearningPhase
    ? lessonResponseWithPhaseNumbers
    : setAllPhaseNumbersToZero(lessonResponseWithPhaseNumbers);
};

const mapPollResponse = (document: GeneratePollPayload): ResponseSelection<PollResponse> => {
  return document.polls.map((question, index) => ({
    tempId: `poll-question-${index}-${simpleIdentifierForTitle(question.statement)}`,
    statement: question.statement,
    options: question.answers,
  }));
};

const mapQuizResponse = (document: GenerateMultipleChoiceQuestionsPayload): ResponseSelection<QuizResponse> => {
  return document.questions.map((question, index) => ({
    tempId: `quiz-question-${index}-${simpleIdentifierForTitle(question.question)}`,
    question: question.question,
    answers: question.answers,
    correctAnswer: question.correctAnswers,
  }));
};

const activatePreviousKnowledgeSlide = (word?: string): WordWebResponse => ({
  tempId: 'activate-previous-knowledge-ticket-1',
  type: 'wordweb',
  word: word ?? '',
  phaseNumber: 1000,
});

const coverSlide = (name?: string): SlideResponse => ({
  tempId: 'cover-slide',
  type: 'slide',
  header: name ?? '',
  layout: 'cover',
  text: '',
  note: '',
  phaseNumber: 1000,
});

const getExitTicketSlideData = (
  t: TFunction,
  notesEnabled: boolean,
  outputLanguage: Language
): OpenQuestionResponse[] => {
  if (outputLanguage === undefined || outputLanguage === 'EN') {
    outputLanguage = defaultLanguage;
  }

  const translatedExitTicket = exitTicketTranslations[outputLanguage];

  return [
    {
      tempId: 'exit-ticket-1',
      type: 'openQuestion',
      question: translatedExitTicket.question1 ?? t('exitTicketQuestions.question1'),
      note: notesEnabled ? (translatedExitTicket.question1Note ?? t('exitTicketQuestions.question1Note')) : '',
      phaseNumber: 4000,
    },
    {
      tempId: 'exit-ticket-2',
      type: 'openQuestion',
      question: translatedExitTicket.question2 ?? t('exitTicketQuestions.question2'),
      note: notesEnabled ? (translatedExitTicket.question2Note ?? t('exitTicketQuestions.question2Note')) : '',
      phaseNumber: 4000,
    },
    {
      tempId: 'exit-ticket-3',
      type: 'openQuestion',
      question: translatedExitTicket.question3 ?? t('exitTicketQuestions.question3'),
      note: notesEnabled ? (translatedExitTicket.question3Note ?? t('exitTicketQuestions.question3Note')) : '',
      phaseNumber: 4000,
    },
  ];
};

const setAllPhaseNumbersToZero = (list: ResponseSelection<LessonResponse>): ResponseSelection<LessonResponse> => {
  return list.map((item) => ({
    ...item,
    phaseNumber: noPhaseNumber,
  }));
};

const exitTicketTranslations: Record<
  Exclude<Language, 'EN'>,
  {
    question1: string;
    question1Note: string;
    question2: string;
    question2Note: string;
    question3: string;
    question3Note: string;
  }
> = {
  // English (UK)
  EN_UK: {
    question1: 'Write down 3 things you learned in this lesson.',
    question1Note:
      'Have students enter three things they learned in this lesson. With this, they can indicate their own learning efficiency of this lesson.',
    question2: 'Write down 2 things you want to know more about.',
    question2Note:
      'Here, students enter two things they would like to know more about. This not only increases involvement but also gives them more ownership.',
    question3: "Ask 1 question about something you haven't quite understood yet.",
    question3Note:
      'The students indicate here (in question form) with which part of the material they still have difficulty. For the teacher, this not only provides insight into the extent to which the students understand/master the material, but also a good starting point for the next lesson.',
  },
  // English (US)
  EN_US: {
    question1: 'Write down 3 things you learned in this lesson.',
    question1Note:
      'Have students enter three things they learned in this lesson. With this, they can indicate their own learning efficiency of this lesson.',
    question2: 'Write down 2 things you want to know more about.',
    question2Note:
      'Here, students enter two things they would like to know more about. This not only increases involvement but also gives them more ownership.',
    question3: "Ask 1 question about something you haven't quite understood yet.",
    question3Note:
      'The students indicate here (in question form) with which part of the material they still have difficulty. For the teacher, this not only provides insight into the extent to which the students understand/master the material, but also a good starting point for the next lesson.',
  },
  // Dutch
  NL: {
    question1: 'Schrijf 3 dingen op die je deze les hebt geleerd.',
    question1Note:
      'De leerlingen voeren hier drie dingen in die ze in deze les hebben geleerd. Hiermee geven ze aan wat hun eigen leerrendement van deze les is.',
    question2: 'Schrijf 2 dingen op waarover je meer wilt weten.',
    question2Note:
      'De leerlingen voeren hier twee dingen in waarover ze meer zouden willen weten. Hiermee vergroot je niet alleen betrokkenheid, maar geef je hen ook meer eigenaarschap.',
    question3: 'Stel 1 vraag over iets dat je nog niet zo goed hebt begrepen.',
    question3Note:
      'De leerlingen geven hier (in vraagvorm) aan met welk onderdeel van de stof ze nog moeite. Voor de docent biedt dit niet alleen inzicht in de mate waarin de stof de leerlingen begrijpen/beheersen, maar ook een goed startpunt voor een volgende les.',
  },
  // German
  DE: {
    question1: 'Schreibe 3 Dinge auf, die Du in dieser Lektion gelernt hast.',
    question1Note:
      'Die Schüler geben hier drei Dinge ein, die sie in dieser Lektion gelernt haben. Damit geben sie an, was ihr eigenes Lernergebnis dieser Lektion ist.',
    question2: 'Schreibe 2 Dinge auf, über die Du mehr wissen möchtest.',
    question2Note:
      'Die Schüler geben hier zwei Dinge ein, über die sie mehr wissen möchten. Damit erhöhen Sie nicht nur das Engagement, sondern geben Sie den Schülern auch mehr Eigenverantwortung.',
    question3: 'Stelle Dir 1 Frage zu etwas, das Du noch nicht so gut verstanden hast.',
    question3Note:
      'Die Schüler geben hier (in Form einer Frage) an, mit welchem Teil des Materials sie noch Schwierigkeiten haben. Für den Lehrer bietet dies nicht nur Einblick in das Verständnis/Niveau der Schüler, sondern auch einen guten Ausgangspunkt für die nächste Lektion.',
  },
  // French
  FR: {
    question1: 'Écrivez 3 choses que vous avez apprises lors de cette leçon.',
    question1Note:
      "Les élèves écrivent trois choses qu'ils ont apprises au cours de cette leçon. Cela leur permet de signaler leur propre rendement d'apprentissage de cette leçon.",
    question2: 'Écrivez 2 choses sur lesquelles vous souhaitez en savoir plus.',
    question2Note:
      'Les élèves écrivent deux choses sur lesquelles ils aimeraient en savoir plus. Cela non seulement augmente leur engagement, mais leur donne également plus de responsabilité.',
    question3: "Posez 1 question sur quelque chose que vous n'avez pas bien compris.",
    question3Note:
      "Les élèves indiquent (sous forme de question) la partie de la matière qui leur pose encore des difficultés. Pour l'enseignant, cela offre non seulement un aperçu du niveau de compréhension/maîtrise des élèves de la matière, mais aussi un bon point de départ pour la prochaine leçon.",
  },
  // Spanish
  ES: {
    question1: 'Escribe 3 cosas que hayas aprendido en esta lección',
    question1Note:
      'Los alumnos escriben aquí 3 cosas que han aprendido en esta lección. Al hacerlo, indican su propio aprendizaje de esta lección.',
    question2: 'Escribe 2 cosas sobre las que te gustaría aprender más.',
    question2Note:
      'Aquí los alumnos introducen dos cosas sobre las que les gustaría saber más. Esto no sólo aumenta el compromiso, sino también el nivel de involucración.',
    question3: 'Haz 1 pregunta sobre algo que aún no has entendido muy bien',
    question3Note:
      'Aquí los alumnos indican (en forma de pregunta) con qué parte del material aún tienen dificultades. Para el profesor, esto no sólo proporciona una idea de hasta qué punto los alumnos comprenden/manejan el material, sino que es también un buen punto de partida para una lección posterior.',
  },
  // Welsh
  CY: {
    question1: 'Ysgrifennwch 3 pheth a ddysgoch yn y wers hon.',
    question1Note:
      "Gofynnwch i fyfyrwyr nodi tri pheth a ddysgon nhw yn y wers hon. Gyda hyn, gallant nodi effeithlonrwydd eu dysgu eu hunain o'r wers hon.",
    question2: 'Ysgrifennwch 2 beth yr hoffech wybod mwy amdanynt.',
    question2Note:
      'Yma, mae myfyrwyr yn nodi dau beth y byddent yn hoffi gwybod mwy amdanynt. Mae hyn nid yn unig yn cynyddu cyfranogiad ond hefyd yn rhoi mwy o berchnogaeth iddynt.',
    question3: "Gofynnwch 1 cwestiwn am rywbeth nad ydych wedi'i ddeall yn iawn eto.",
    question3Note:
      'Mae’r myfyrwyr yn nodi yma (mewn ffurf cwestiwn) pa ran o’r deunydd y mae ganddynt anhawster ag ef o hyd. I’r athro, mae hyn nid yn unig yn darparu mewnwelediad i’r graddau y mae’r myfyrwyr yn deall/meistroli’r deunydd ond hefyd yn fan cychwyn da ar gyfer y wers nesaf.',
  },
  // Gaelic (Scottish Gaelic)
  GD: {
    question1: 'Sgrìobh sìos 3 rud a dh’ionnsaich thu san leasan seo.',
    question1Note:
      'Leig le oileanaich trì rudan a dh’ionnsaich iad san leasan seo a chur a-steach. Le seo, faodaidh iad comharrachadh dè cho math ’s a thuig iad an leasan seo.',
    question2: 'Sgrìobh sìos 2 rud a bu mhath leat barrachd fhaighinn a-mach mu dheidhinn.',
    question2Note:
      'An seo, bidh oileanaich a’ cur a-steach dà rud a bu mhath leotha barrachd fhaighinn a-mach mu dheidhinn. Bidh seo chan ann a-mhàin a’ meudachadh an com-pàirteachadh, ach a’ toirt barrachd seilbh dhaibh cuideachd.',
    question3: 'Cuir 1 cheist mu rudeigin nach do thuig thu gu math fhathast.',
    question3Note:
      'An seo, tha na h-oileanaich a’ comharrachadh (ann an cruth ceist) dè an roinn den stuth leis a bheil iad fhathast a’ strì. Dha’n tidsear, tha seo chan ann a-mhàin a’ toirt sealladh air dè cho math ’s a tha an luchd-ionnsachaidh a’ tuigsinn/am maighstireachd air an stuth, ach cuideachd mar thoiseach tòiseachaidh math airson an ath leasan.',
  },
  // Portuguese
  PT: {
    question1: 'Partilha 3 aprendizagens desta aula.',
    question1Note:
      'Peça aos alunos que escrevam três aprendizagens que aprenderam nesta aula. Com isso, eles podem indicar a sua própria eficiência de aprendizagem nesta aula.',
    question2: 'Partilha duas coisas sobre as quais queres saber mais.',
    question2Note:
      'Aqui, os alunos escrevem duas coisas sobre as quais gostariam de saber mais. Isso não apenas aumenta o envolvimento, mas também lhes dá mais autonomia.',
    question3: 'Que pergunta gostarias de fazer para aprofundar ainda mais o teu conhecimento?',
    question3Note:
      'Os alunos indicam aqui (em forma de pergunta) com qual parte do material ainda têm dificuldade. Para o/a professor/a, isso não apenas fornece uma visão sobre o nível de compreensão dos alunos, mas também um bom ponto de partida para a próxima aula.',
  },
  // Italian
  IT: {
    question1: 'Scrivi 3 cose che hai imparato in questa lezione.',
    question1Note:
      'Per confermare che gli studenti abbiano prestato attenzione e che ció che hanno capito sia corretto.',
    question2: 'Scrivi 2 cose di cui vuoi sapere di più.',
    question2Note: 'Per aumentare il senso di coinvolgimento e di autonomia degli studenti.',
    question3: 'Fai 1 domanda su qualcosa che non hai ancora capito bene.',
    question3Note:
      "Offre un'idea generale del livello di comprensione degli studenti. Utile a identificare parti che generano confusione agli studenti e come punto di partenza per lezioni successive.",
  },
  // Chinese
  ZH: {
    question1: '写下你在这节课中学到的3件事。',
    question1Note: '让学生输入他们在这节课中学到的三件事。这可以帮助他们评估自己的学习效果。',
    question2: '写下2件你想了解更多的事情。',
    question2Note: '在这里，学生输入他们想了解更多的两件事。这不仅可以增加他们的参与感，还能增强他们的学习主动性。',
    question3: '提出1个你还没有完全理解的问题。',
    question3Note:
      '学生在这里（以问题形式）指出他们仍然感到困难的知识点。这不仅能帮助教师了解学生的掌握情况，还可以作为下一节课的切入点。',
  },
  // Mongolian
  MN: {
    question1: 'Энэ хичээлээр сурсан 3 зүйлээ бичнэ үү.',
    question1Note:
      'Сурагчид энэ хичээлээр сурсан гурван зүйлээ бичнэ. Ингэснээр тэд өөрийн суралцах үр дүнгээ тодорхойлж чадна.',
    question2: 'Илүү ихийг мэдэхийг хүсч буй 2 зүйлээ бичнэ үү.',
    question2Note:
      'Энд сурагчид илүү ихийг мэдэхийг хүсч буй хоёр зүйлээ бичнэ. Энэ нь тэдний оролцоог нэмэгдүүлж, хичээлдээ илүү эзэн болж өгнө.',
    question3: 'Ойлгоогүй байгаа 1 асуултыг бичнэ үү.',
    question3Note:
      'Сурагчид энд (асуултын хэлбэрээр) ойлгоход хэцүү байгаа хэсгээ илэрхийлнэ. Энэ нь багшид сурагчдын ойлголтын түвшинг үнэлэхэд туслах бөгөөд дараагийн хичээлээ төлөвлөхөд ашигтай байж болно.',
  },
  // Hindi
  HI: {
    question1: 'इस पाठ में आपने सीखी गई 3 चीजें लिखें।',
    question1Note: 'छात्र यहां इस पाठ में सीखी गई तीन चीजें दर्ज करें। इससे वे अपने सीखने की दक्षता को दर्शा सकते हैं।',
    question2: '2 चीजें लिखें जिनके बारे में आप और अधिक जानना चाहते हैं।',
    question2Note:
      'यहां छात्र दो चीजें दर्ज करेंगे जिनके बारे में वे अधिक जानना चाहते हैं। इससे उनकी भागीदारी बढ़ेगी और वे अधिक जिम्मेदार महसूस करेंगे।',
    question3: 'ऐसा 1 प्रश्न पूछें जिसे आप अभी तक पूरी तरह से नहीं समझ पाए हैं।',
    question3Note:
      'छात्र यहां (प्रश्न के रूप में) बताएंगे कि उन्हें किस भाग को समझने में कठिनाई हो रही है। शिक्षक के लिए यह उनकी समझ का मूल्यांकन करने और अगली कक्षा के लिए योजना बनाने में सहायक होगा।',
  },
  // Japanese
  JA: {
    question1: 'このレッスンで学んだ3つのことを書きましょう。',
    question1Note: '生徒がこのレッスンで学んだ3つのことを入力します。これにより、彼らは自分の学習効果を確認できます。',
    question2: 'もっと知りたい2つのことを書きましょう。',
    question2Note: '生徒がもっと知りたい2つのことを入力します。これにより、関与が高まり、学習の主体性が向上します。',
    question3: 'まだよく理解できていないことについて1つ質問をしてください。',
    question3Note:
      '生徒はここで（質問形式で）まだ理解が不十分な部分を示します。教師にとって、これは生徒の理解度を評価し、次の授業の計画を立てるための良い指標になります。',
  },
};

function simpleIdentifierForTitle(title: string) {
  return title.toLowerCase().substring(0, 15).replace(/\s/, '_');
}

interface PassPromptBetweenMaiaModesParams {
  lessonType: MaiaLessonType;
  setLessonType: React.Dispatch<React.SetStateAction<MaiaLessonType | undefined>>;
  prompt: string | undefined;
  setTempSavedPrompt: React.Dispatch<React.SetStateAction<string | undefined>>;
}

export function passPromptBetweenMaiaModes({
  prompt,
  lessonType,
  setLessonType,
  setTempSavedPrompt,
}: PassPromptBetweenMaiaModesParams) {
  setLessonType(lessonType);
  setTempSavedPrompt(prompt);
}
